// Lightweight interface-only i18n. Dish names/prices/business info are NEVER auto-translated.
// Only Module 1 UI keys have EN copy; HI/MR/TA are scaffolded (structure present, translations left empty
// so future modules can supply owner-verified translations without a rebuild).

const en = {
  brand: "TEZZ KHANA CATERING SERVICE",
  nav: {
    home: "Home",
    menu: "Menu",
    packages: "Packages",
    build: "Build Your Menu",
    quote: "Request Quote",
    contact: "Contact",
    cart: "Cart",
    admin: "Admin",
  },
  hero: {
    eyebrow: "Premium Indian Event Catering",
    title_a: "Curate a feast",
    title_b: "worthy of the occasion.",
    subtitle:
      "Weddings, receptions, corporate events, and family celebrations — served with the finest hospitality.",
    start_order: "Start Your Order",
    explore_menu: "Explore Menu",
    build_menu: "Build Your Menu",
    request_quote: "Request Custom Quote",
  },
  event: {
    select: "What is the occasion?",
    guest_count: "Expected guests",
    continue: "Continue",
    types: {
      wedding: "Wedding",
      reception: "Reception",
      corporate: "Corporate",
      birthday: "Birthday",
      housewarming: "Housewarming",
      other: "Other",
    },
  },
  search: {
    placeholder: "Search dishes, categories or packages…",
    no_results: "No matches yet. Try a different term.",
  },
  menu: {
    title: "Our Menu",
    subtitle: "Browse dishes by category. Real photos will appear as they are uploaded by the owner.",
    price_on_request: "Price on request",
    add: "Add",
    remove: "Remove",
    unavailable: "Unavailable",
    empty_category: "No dishes published in this category yet.",
    empty_all:
      "No dishes have been published yet. The owner will populate categories, dishes and images from the admin panel.",
    popular: "Popular",
  },
  packages: {
    title: "Curated Packages",
    subtitle: "Silver, Gold, Premium and Custom builds — priced only after owner confirmation.",
    empty:
      "Packages are being finalised. Please Request a Custom Quote and our team will craft one for you.",
    min_guests: "Minimum",
    guests: "guests",
    included: "Included",
    optional: "Optional add-ons",
    price_on_request: "Price on request",
  },
  build: {
    title: "Build Your Menu",
    step_event: "Choose the event",
    step_guests: "How many guests?",
    step_pick: "Pick your dishes",
    step_review: "Review your selection",
    back: "Back",
    next: "Next",
    review: "Review",
    go_to_cart: "Go to Cart",
  },
  cart: {
    title: "Your Selection",
    empty: "Your selection is empty. Add dishes from the Menu or Build Your Menu.",
    pricing_note:
      "Pricing will be calculated after guest count and catering options are selected. Our team will confirm the final quote.",
    quantity: "Quantity",
    remove_item: "Remove",
    clear: "Clear all",
    request_quote: "Request Custom Quote",
    event: "Event",
    guests: "Guests",
  },
  quote: {
    title: "Request a Custom Quote",
    subtitle:
      "Share a few details and our team will get back to you with a tailored proposal. No auto-pricing — every quote is confirmed by the owner.",
    name: "Full name",
    mobile: "Mobile number",
    whatsapp: "WhatsApp number (optional)",
    email: "Email (optional)",
    event_type: "Event type",
    date: "Event date",
    time: "Event time",
    venue: "Venue / Location",
    guest_count: "Guest count",
    special: "Special requirements (optional)",
    submit: "Send Enquiry",
    submitting: "Sending…",
    success: "Enquiry received. Our team will reach out shortly.",
    error: "Something went wrong. Please try again.",
    required: "Please fill in your name and mobile number.",
    selected_menu: "Selected menu",
    no_menu_selected: "No dishes selected yet.",
  },
  contact: {
    title: "Get in touch",
    subtitle:
      "Reach out for enquiries, tastings, and event consultations.",
    whatsapp_hint: "WhatsApp will be enabled once the owner configures the business number.",
    phone: "Phone",
    email: "Email",
    address: "Address",
    not_configured: "To be configured",
  },
  footer: {
    tagline: "Crafted vegetarian catering for every celebration. Weddings, receptions, corporate events, birthdays and family functions across Mumbai, Navi Mumbai & Thane.",
    modules: "Module 2 — Build Your Menu & pricing engine live. Owner-approved rates apply; final quotation from Tezz Khana confirms all charges.",
    made_with: "© 2026 Tezz Khana Catering Service.",
  },
  admin: {
    login_title: "Admin Sign in",
    login_subtitle: "Only authorised owners can access the Tezz Khana backend.",
    email: "Email",
    password: "Password",
    sign_in: "Sign in",
    signing_in: "Signing in…",
    invalid: "Invalid credentials",
    force_change_title: "Change your temporary password",
    force_change_subtitle: "For security, set a new password before continuing.",
    current: "Current (temporary) password",
    new_password: "New password",
    confirm_password: "Confirm new password",
    save: "Save",
    saving: "Saving…",
    change_success: "Password updated.",
    change_mismatch: "New passwords do not match.",
    change_short: "Password must be at least 10 characters.",
    dashboard: "Dashboard",
    categories: "Categories",
    dishes: "Dishes",
    packages: "Packages",
    enquiries: "Enquiries",
    settings: "Settings",
    logout: "Log out",
    coming_soon: "Coming in future modules",
  },
  common: {
    yes: "Yes",
    no: "No",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    close: "Close",
    unavailable: "Unavailable",
    published: "Published",
    unpublished: "Unpublished",
  },
  account: {
    signin: "Sign in",
    register: "Create account",
    logout: "Log out",
    dashboard: "Dashboard",
    profile: "Profile",
    identifier: "Email or Mobile",
    password: "Password",
    welcome: "Welcome",
  },
};

// Scaffolded — future modules will supply owner-verified translations. Falls back to EN.
const hi = {};
const mr = {};
const ta = {};

const bundles = { en, hi, mr, ta };

function get(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function makeT(lang) {
  return (key) => {
    const active = bundles[lang] || {};
    const v = get(active, key);
    if (v !== undefined && v !== "") return v;
    const en_v = get(en, key);
    return en_v !== undefined ? en_v : key;
  };
}

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "mr", label: "Marathi", native: "मराठी" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
];
